import { useState, useEffect } from 'react';
import type { Group, Template } from '@/types';
import type { Campaign } from '@/mocks/campaignsData';
import type { MediaType } from '@/types';
import { supabase } from '@/lib/supabase';

const SESSION_KEY = 'campaign_draft';

function isYouTubeUrl(url: string): boolean {
  try {
    const u = new URL(url);
    return u.hostname === 'youtu.be' || u.hostname === 'www.youtube.com' || u.hostname === 'youtube.com' || u.hostname === 'm.youtube.com';
  } catch {
    return false;
  }
}

function sanitizeFileName(name: string): string {
  return name
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9._-]/g, '_')
    .replace(/_{2,}/g, '_')
    .toLowerCase();
}

const mediaTypeOptions: { value: MediaType; label: string; icon: string }[] = [
  { value: 'none', label: 'Texto', icon: 'ri-chat-1-line' },
  { value: 'image', label: 'Imagem', icon: 'ri-image-line' },
  { value: 'video', label: 'Vídeo', icon: 'ri-video-line' },
  { value: 'audio', label: 'Áudio', icon: 'ri-volume-up-line' },
  { value: 'document', label: 'Documento', icon: 'ri-file-text-line' },
];

interface CampaignModalProps {
  campaign: Campaign | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: Omit<Campaign, 'id' | 'created_at' | 'updated_at' | 'sent_count' | 'delivered_count' | 'read_count' | 'replied_count' | 'failed_count' | 'started_at' | 'completed_at'>) => Promise<void>;
  groups: Group[];
  templates: Template[];
  saving?: boolean;
  saveError?: string | null;
}

export default function CampaignModal({ campaign, isOpen, onClose, onSave, groups, templates, saving, saveError }: CampaignModalProps) {
  const [name, setName] = useState(campaign?.name ?? '');
  const [messageText, setMessageText] = useState(campaign?.message_text ?? '');
  const [status, setStatus] = useState<Campaign['status']>(campaign?.status ?? 'rascunho');
  const [targetType, setTargetType] = useState<Campaign['target_type']>(campaign?.target_type ?? 'grupos');
  const [selectedGroups, setSelectedGroups] = useState<string[]>(campaign?.target_groups ?? []);
  const [scheduledAt, setScheduledAt] = useState(campaign?.scheduled_at ?? '');
  const [useTemplate, setUseTemplate] = useState('');
  const [mediaType, setMediaType] = useState<MediaType>((campaign?.media_type as MediaType) ?? 'none');
  const [mediaUrl, setMediaUrl] = useState(campaign?.media_url ?? '');
  const [linkUrl, setLinkUrl] = useState(campaign?.link_url ?? '');

  // 🎲 Message variants (up to 5 extra messages for randomization)
  const [variant1, setVariant1] = useState((campaign as any)?.message_variants?.[0] ?? '');
  const [variant2, setVariant2] = useState((campaign as any)?.message_variants?.[1] ?? '');
  const [variant3, setVariant3] = useState((campaign as any)?.message_variants?.[2] ?? '');
  const [variant4, setVariant4] = useState((campaign as any)?.message_variants?.[3] ?? '');
  const [variant5, setVariant5] = useState((campaign as any)?.message_variants?.[4] ?? '');

  // 🛡️ Daily limit
  const [dailyLimit, setDailyLimit] = useState((campaign as any)?.daily_limit ?? 0);
  const [showAntiSpamInfo, setShowAntiSpamInfo] = useState(false);
  const [showVariants, setShowVariants] = useState(((campaign as any)?.message_variants?.length || 0) > 0);

  // File upload states (works for video, image, audio, document)
  const [fileMode, setFileMode] = useState<'link' | 'upload'>('link');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  // 💾 Restore draft from sessionStorage on mount (only for new campaigns, not editing)
  useEffect(() => {
    if (isOpen && !campaign) {
      try {
        const draft = sessionStorage.getItem(SESSION_KEY);
        if (draft) {
          const parsed = JSON.parse(draft);
          if (parsed.name) setName(parsed.name);
          if (parsed.messageText) setMessageText(parsed.messageText);
          if (parsed.status) setStatus(parsed.status);
          if (parsed.targetType) setTargetType(parsed.targetType);
          if (parsed.selectedGroups) setSelectedGroups(parsed.selectedGroups);
          if (parsed.scheduledAt) setScheduledAt(parsed.scheduledAt);
          if (parsed.useTemplate) setUseTemplate(parsed.useTemplate);
          if (parsed.mediaType) setMediaType(parsed.mediaType);
          if (parsed.mediaUrl) setMediaUrl(parsed.mediaUrl);
          if (parsed.linkUrl) setLinkUrl(parsed.linkUrl);
          if (parsed.variant1 !== undefined) setVariant1(parsed.variant1);
          if (parsed.variant2 !== undefined) setVariant2(parsed.variant2);
          if (parsed.variant3 !== undefined) setVariant3(parsed.variant3);
          if (parsed.variant4 !== undefined) setVariant4(parsed.variant4);
          if (parsed.variant5 !== undefined) setVariant5(parsed.variant5);
          if (parsed.dailyLimit !== undefined) setDailyLimit(parsed.dailyLimit);
          if (parsed.showAntiSpamInfo) setShowAntiSpamInfo(parsed.showAntiSpamInfo);
          if (parsed.showVariants) setShowVariants(parsed.showVariants);
          if (parsed.fileMode) setFileMode(parsed.fileMode);
        }
      } catch {
        // ignore parse errors
      }
    }
  }, [isOpen, campaign]);

  // 💾 Save draft to sessionStorage whenever it changes (only for new campaigns)
  useEffect(() => {
    if (isOpen && !campaign) {
      const draft = {
        name,
        messageText,
        status,
        targetType,
        selectedGroups,
        scheduledAt,
        useTemplate,
        mediaType,
        mediaUrl,
        linkUrl,
        variant1,
        variant2,
        variant3,
        variant4,
        variant5,
        dailyLimit,
        showAntiSpamInfo,
        showVariants,
        fileMode,
      };
      sessionStorage.setItem(SESSION_KEY, JSON.stringify(draft));
    }
  }, [isOpen, campaign, name, messageText, status, targetType, selectedGroups, scheduledAt, useTemplate, mediaType, mediaUrl, linkUrl, variant1, variant2, variant3, variant4, variant5, dailyLimit, showAntiSpamInfo, showVariants, fileMode]);

  if (!isOpen) return null;

  const applyTemplate = (templateId: string) => {
    const tpl = templates.find((t) => t.id === templateId);
    if (tpl) {
      setMessageText(tpl.content);
      setUseTemplate(templateId);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    if (!file) return;

    // Validate by media type
    const maxSizeMap: Record<MediaType, number> = {
      none: 0,
      image: 5 * 1024 * 1024,
      video: 16 * 1024 * 1024,
      audio: 16 * 1024 * 1024,
      document: 100 * 1024 * 1024,
    };

    const allowedTypesMap: Record<MediaType, string[]> = {
      none: [],
      image: ['image/jpeg', 'image/png', 'image/webp', 'image/gif'],
      video: ['video/mp4', 'video/webm'],
      audio: ['audio/mpeg', 'audio/mp3', 'audio/ogg', 'audio/wav', 'audio/webm'],
      document: [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-powerpoint',
        'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        'text/plain',
      ],
    };

    const maxSize = maxSizeMap[mediaType] || 16 * 1024 * 1024;
    const allowedTypes = allowedTypesMap[mediaType] || [];

    if (file.size > maxSize) {
      const maxMb = (maxSize / 1024 / 1024).toFixed(0);
      setUploadError(`Arquivo muito grande. Máximo ${maxMb}MB para WhatsApp.`);
      return;
    }
    if (allowedTypes.length > 0 && !allowedTypes.includes(file.type)) {
      setUploadError(`Tipo de arquivo não suportado. Formatos aceitos: ${allowedTypes.join(', ')}`);
      return;
    }

    setSelectedFile(file);
    setUploadError(null);
  };

  const uploadFile = async (): Promise<string | null> => {
    if (!selectedFile) return null;

    setUploading(true);
    setUploadProgress(0);
    setUploadError(null);

    try {
      const sanitized = sanitizeFileName(selectedFile.name);
      const path = `${Date.now()}_${sanitized}`;

      const { data, error } = await supabase.storage
        .from('campaign-media')
        .upload(path, selectedFile, {
          cacheControl: '3600',
          upsert: false,
        });

      if (error) {
        setUploadError(`Erro no upload: ${error.message}`);
        setUploading(false);
        return null;
      }

      const { data: publicUrlData } = supabase.storage
        .from('campaign-media')
        .getPublicUrl(data.path);

      setUploading(false);
      setUploadProgress(100);
      return publicUrlData.publicUrl;
    } catch (err: unknown) {
      setUploadError(err instanceof Error ? err.message : 'Erro ao fazer upload');
      setUploading(false);
      return null;
    }
  };

  const handleSave = async () => {
    if (!name.trim() || !messageText.trim()) return;

    let finalMediaUrl = mediaUrl.trim();

    if (mediaType !== 'none' && fileMode === 'upload' && selectedFile) {
      const uploadedUrl = await uploadFile();
      if (!uploadedUrl) return;
      finalMediaUrl = uploadedUrl;
    }

    // 🎲 Build message variants array (filter empty)
    const allVariants = [variant1, variant2, variant3, variant4, variant5]
      .map(v => v.trim())
      .filter(v => v.length > 0);

    const totalContacts = targetType === 'todos' ? 5000 : selectedGroups.length * 300;
    await onSave({
      name: name.trim(),
      message_text: messageText.trim(),
      link_url: linkUrl.trim() || null,
      status,
      total_contacts: Math.max(totalContacts, selectedGroups.length * 50),
      target_groups: selectedGroups,
      target_type: targetType,
      scheduled_at: scheduledAt || null,
      media_type: mediaType === 'none' ? null : mediaType,
      media_url: mediaType === 'none' ? null : finalMediaUrl || null,
      message_variants: allVariants.length > 0 ? allVariants : null,
      daily_limit: dailyLimit > 0 ? dailyLimit : null,
    } as any);
  };

  const handleClose = () => {
    sessionStorage.removeItem(SESSION_KEY);
    onClose();
  };

  const toggleGroup = (groupName: string) => {
    setSelectedGroups((prev) =>
      prev.includes(groupName) ? prev.filter((g) => g !== groupName) : [...prev, groupName]
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" />
      <div className="relative bg-white rounded-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-background-100">
          <h3 className="text-lg font-semibold text-foreground-950">
            {campaign ? 'Editar campanha' : 'Nova campanha'}
          </h3>
          <button onClick={handleClose} className="text-foreground-400 hover:text-foreground-600 cursor-pointer">
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground-700 mb-1.5">Nome da campanha</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2.5 bg-white border border-background-200 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
              placeholder="Ex: Black Friday 2025"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground-700 mb-1.5">Usar template</label>
            <select
              value={useTemplate}
              onChange={(e) => applyTemplate(e.target.value)}
              className="w-full px-3 py-2.5 bg-white border border-background-200 rounded-lg text-sm text-foreground-900 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
            >
              <option value="">Escrever mensagem manualmente</option>
              {templates.map((tpl) => (
                <option key={tpl.id} value={tpl.id}>{tpl.name}</option>
              ))}
            </select>
          </div>

          {/* Media type selector */}
          <div>
            <label className="block text-sm font-medium text-foreground-700 mb-1.5">Tipo de mídia (opcional)</label>
            <div className="flex flex-wrap gap-2 mb-2">
              {mediaTypeOptions.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => {
                    setMediaType(opt.value);
                    setFileMode('link');
                    setSelectedFile(null);
                    setUploadError(null);
                  }}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer border whitespace-nowrap ${
                    mediaType === opt.value
                      ? 'bg-primary-50 text-primary-700 border-primary-300'
                      : 'bg-white text-foreground-600 border-background-200 hover:bg-background-50'
                  }`}
                >
                  <i className={`${opt.icon} mr-1`} />
                  {opt.label}
                </button>
              ))}
            </div>

            {mediaType !== 'none' && (
              <div className="space-y-2">
                {/* Upload / Link toggle for all media types */}
                <div className="flex gap-2 mb-2">
                  <button
                    type="button"
                    onClick={() => { setFileMode('upload'); setMediaUrl(''); setUploadError(null); }}
                    className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer border ${
                      fileMode === 'upload'
                        ? 'bg-accent-50 text-accent-700 border-accent-300'
                        : 'bg-white text-foreground-600 border-background-200 hover:bg-background-50'
                    }`}
                  >
                    <i className="ri-upload-cloud-line mr-1" />
                    Enviar arquivo
                  </button>
                  <button
                    type="button"
                    onClick={() => { setFileMode('link'); setSelectedFile(null); setUploadError(null); }}
                    className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer border ${
                      fileMode === 'link'
                        ? 'bg-accent-50 text-accent-700 border-accent-300'
                        : 'bg-white text-foreground-600 border-background-200 hover:bg-background-50'
                    }`}
                  >
                    <i className="ri-link mr-1" />
                    Usar link
                  </button>
                </div>

                {fileMode === 'upload' ? (
                  <div>
                    <div className="relative">
                      <input
                        type="file"
                        accept={
                          mediaType === 'image'
                            ? 'image/jpeg,image/png,image/webp,image/gif'
                            : mediaType === 'video'
                              ? 'video/mp4,video/webm'
                              : mediaType === 'audio'
                                ? 'audio/mpeg,audio/mp3,audio/ogg,audio/wav,audio/webm'
                                : '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt'
                        }
                        onChange={handleFileSelect}
                        id="media-upload"
                        className="hidden"
                      />
                      <label
                        htmlFor="media-upload"
                        className="flex items-center justify-center gap-2 w-full px-4 py-3 border-2 border-dashed border-background-300 rounded-lg cursor-pointer hover:border-primary-400 hover:bg-primary-50/50 transition-colors"
                      >
                        <i className="ri-upload-cloud-2-line text-xl text-primary-500" />
                        <span className="text-sm text-foreground-600">
                          {selectedFile ? selectedFile.name : `Clique para selecionar um arquivo de ${mediaType === 'image' ? 'imagem' : mediaType === 'video' ? 'vídeo' : mediaType === 'audio' ? 'áudio' : 'documento'}`}
                        </span>
                      </label>
                    </div>

                    {selectedFile && (
                      <div className="mt-2 flex items-center gap-2 text-sm text-foreground-600">
                        <i className={`${
                          mediaType === 'image' ? 'ri-image-line' :
                          mediaType === 'video' ? 'ri-file-video-line' :
                          mediaType === 'audio' ? 'ri-volume-up-line' :
                          'ri-file-text-line'
                        } text-primary-500`} />
                        <span className="flex-1 truncate">{selectedFile.name}</span>
                        <span className="text-xs text-foreground-400">{(selectedFile.size / 1024 / 1024).toFixed(2)} MB</span>
                        <button
                          type="button"
                          onClick={() => { setSelectedFile(null); setUploadError(null); }}
                          className="text-foreground-400 hover:text-red-500 cursor-pointer"
                        >
                          <i className="ri-close-line" />
                        </button>
                      </div>
                    )}

                    {uploading && (
                      <div className="mt-2">
                        <div className="w-full h-2 bg-background-100 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-primary-500 rounded-full transition-all duration-300"
                            style={{ width: `${uploadProgress}%` }}
                          />
                        </div>
                        <p className="text-xs text-foreground-500 mt-1">Fazendo upload... {uploadProgress}%</p>
                      </div>
                    )}

                    <p className="text-xs text-foreground-400 mt-1">
                      {mediaType === 'image'
                        ? 'Máximo 5MB. Formatos: JPG, PNG, WEBP, GIF.'
                        : mediaType === 'video'
                          ? 'Máximo 16MB. Formatos: MP4, WEBM.'
                          : mediaType === 'audio'
                            ? 'Máximo 16MB. Formatos: MP3, OGG, WAV.'
                            : 'Máximo 100MB. Formatos: PDF, DOC, DOCX, XLS, XLSX, PPT, TXT.'}
                    </p>
                  </div>
                ) : (
                  <div>
                    <input
                      type="url"
                      value={mediaUrl}
                      onChange={(e) => setMediaUrl(e.target.value)}
                      className="w-full px-3 py-2.5 bg-white border border-background-200 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all font-mono"
                      placeholder={
                        mediaType === 'image'
                          ? 'https://... (URL da imagem — JPG, PNG, WEBP)'
                          : mediaType === 'video'
                            ? 'https://... (URL do vídeo — MP4 ou YouTube)'
                            : mediaType === 'audio'
                              ? 'https://... (URL do áudio — MP3, OGG)'
                              : 'https://... (URL do documento — PDF, DOCX, XLSX)'
                      }
                    />
                    <p className="text-xs text-foreground-400 mt-1">
                      {mediaType === 'image'
                        ? 'Cole o link de uma imagem pública. Máx 5MB no WhatsApp.'
                        : mediaType === 'video'
                          ? isYouTubeUrl(mediaUrl)
                            ? 'Link do YouTube detectado. O WhatsApp gerará um card de preview com thumbnail e título clicável.'
                            : 'Cole o link de um vídeo público (MP4). Máx 16MB no WhatsApp. Links do YouTube viram preview automático.'
                          : mediaType === 'audio'
                            ? 'Cole o link de um áudio público (MP3, OGG). Máx 16MB no WhatsApp.'
                            : 'Cole o link de um documento público (PDF, DOCX, XLSX, etc.). Máx 100MB no WhatsApp.'}
                    </p>

                    {mediaUrl && (
                      <div className="mt-3 rounded-lg border border-background-200 overflow-hidden bg-background-50">
                        {mediaType === 'image' ? (
                          <img
                            src={mediaUrl}
                            alt="Preview da mídia"
                            className="w-full max-h-[200px] object-cover"
                            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                          />
                        ) : mediaType === 'video' ? (
                          isYouTubeUrl(mediaUrl) ? (
                            <div className="p-4 flex items-center gap-3 text-sm text-foreground-600">
                              <i className="ri-youtube-fill text-3xl text-red-500" />
                              <div className="flex-1 min-w-0">
                                <p className="font-medium truncate">YouTube — Preview no WhatsApp</p>
                                <p className="text-foreground-400 truncate">{mediaUrl}</p>
                              </div>
                            </div>
                          ) : (
                            <div className="p-4 flex items-center gap-3 text-sm text-foreground-500">
                              <i className="ri-video-line text-3xl text-foreground-400" />
                              <span>URL do vídeo: {mediaUrl.length > 50 ? mediaUrl.slice(0, 50) + '...' : mediaUrl}</span>
                            </div>
                          )
                        ) : mediaType === 'audio' ? (
                          <div className="p-4 flex items-center gap-3 text-sm text-foreground-500">
                            <i className="ri-volume-up-line text-3xl text-foreground-400" />
                            <span>URL do áudio: {mediaUrl.length > 50 ? mediaUrl.slice(0, 50) + '...' : mediaUrl}</span>
                          </div>
                        ) : (
                          <div className="p-4 flex items-center gap-3 text-sm text-foreground-500">
                            <i className="ri-file-text-line text-3xl text-foreground-400" />
                            <span>URL do documento: {mediaUrl.length > 50 ? mediaUrl.slice(0, 50) + '...' : mediaUrl}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {uploadError && (
                  <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">
                    <i className="ri-error-warning-line flex-shrink-0" />
                    <span>{uploadError}</span>
                  </div>
                )}
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground-700 mb-1.5">Mensagem</label>
            <textarea
              value={messageText}
              onChange={(e) => setMessageText(e.target.value)}
              rows={5}
              className="w-full px-3 py-2.5 bg-white border border-background-200 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all resize-none"
              placeholder={mediaType !== 'none' ? `Legenda da ${mediaType === 'image' ? 'imagem' : mediaType === 'video' ? 'vídeo' : mediaType === 'audio' ? 'áudio' : 'documento'}...` : 'Digite a mensagem da campanha...'}
            />
            <div className="flex items-center justify-between mt-1">
              <p className="text-xs text-foreground-400">{messageText.length} caracteres</p>
              <button
                type="button"
                onClick={() => setShowAntiSpamInfo(!showAntiSpamInfo)}
                className="text-xs text-amber-600 hover:text-amber-700 flex items-center gap-1 cursor-pointer"
              >
                <i className="ri-shield-check-line" />
                Dica anti-spam
              </button>
            </div>
            {showAntiSpamInfo && (
              <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800 space-y-1">
                <p><strong>Use spintax pra variar o texto:</strong> <code className="bg-amber-100 px-1 rounded">{'{Olá|Oi|Bom dia}'}</code> — o sistema escolhe uma opção aleatória</p>
                <p><strong>Adicione variantes:</strong> escreva até 5 mensagens diferentes abaixo — cada contato recebe uma aleatória</p>
                <p><strong>Delays aleatórios:</strong> entre 3 e 12 segundos entre mensagens pra simular comportamento humano</p>
                <p><strong>Limite diário:</strong> configure um máximo de envios por dia pra evitar bloqueios</p>
              </div>
            )}
          </div>

          {/* 🎲 Message variants */}
          <div>
            <button
              type="button"
              onClick={() => setShowVariants(!showVariants)}
              className="flex items-center gap-2 text-sm font-medium text-foreground-700 hover:text-primary-600 cursor-pointer transition-colors"
            >
              <i className={showVariants ? 'ri-arrow-down-s-line' : 'ri-arrow-right-s-line'} />
              <i className="ri-chat-3-line" />
              Variantes de mensagem (anti-spam)
              {[variant1, variant2, variant3, variant4, variant5].filter(v => v.trim()).length > 0 && (
                <span className="px-1.5 py-0.5 bg-emerald-100 text-emerald-700 text-xs rounded-full font-medium">
                  {[variant1, variant2, variant3, variant4, variant5].filter(v => v.trim()).length}
                </span>
              )}
            </button>
            {showVariants && (
              <div className="mt-3 space-y-2 ml-6">
                <p className="text-xs text-foreground-500">Escreva até 5 variações da mensagem. O sistema escolhe aleatoriamente entre elas + a mensagem principal pra cada contato, evitando que o WhatsApp detecte spam.</p>
                {[
                  { value: variant1, setter: setVariant1, label: 'Variante 1' },
                  { value: variant2, setter: setVariant2, label: 'Variante 2' },
                  { value: variant3, setter: setVariant3, label: 'Variante 3' },
                  { value: variant4, setter: setVariant4, label: 'Variante 4' },
                  { value: variant5, setter: setVariant5, label: 'Variante 5' },
                ].map((v, idx) => (
                  <div key={idx}>
                    <textarea
                      value={v.value}
                      onChange={(e) => v.setter(e.target.value)}
                      rows={2}
                      className="w-full px-3 py-2 bg-white border border-background-200 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-accent-400 focus:ring-2 focus:ring-accent-100 transition-all resize-none"
                      placeholder={`${v.label} (opcional) — ex: mesmo conteúdo com saudação diferente`}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground-700 mb-1.5">Link (opcional)</label>
            <input
              type="url"
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              className="w-full px-3 py-2.5 bg-white border border-background-200 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all font-mono"
              placeholder="https://seu-site.com/promocao"
            />
            <p className="text-xs text-foreground-400 mt-1">O link será incluído na mensagem enviada. O WhatsApp gerará uma prévia automática.</p>
          </div>

          {/* 🛡️ Limite diário (anti-spam) */}
          <div>
            <label className="block text-sm font-medium text-foreground-700 mb-1.5">
              <i className="ri-shield-check-line mr-1 text-amber-500" />
              Limite diário de envios (anti-spam)
            </label>
            <div className="flex items-center gap-3">
              <input
                type="number"
                value={dailyLimit || ''}
                onChange={(e) => setDailyLimit(parseInt(e.target.value) || 0)}
                className="w-32 px-3 py-2.5 bg-white border border-background-200 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                placeholder="200"
                min={0}
                max={1000}
              />
              <span className="text-sm text-foreground-500">mensagens por dia</span>
            </div>
            <p className="text-xs text-foreground-400 mt-1">
              {dailyLimit > 0
                ? `Máximo de ${dailyLimit} envios por dia. Após atingir o limite, a campanha pausa automaticamente e retoma no dia seguinte.`
                : 'Deixe em branco para usar o limite padrão (200/dia). Isso evita bloqueios do WhatsApp por envio excessivo.'}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">Status inicial</label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value as Campaign['status'])}
                className="w-full px-3 py-2.5 bg-white border border-background-200 rounded-lg text-sm text-foreground-900 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
              >
                <option value="rascunho">Rascunho</option>
                <option value="agendada">Agendada</option>
                <option value="ativa">Ativa (enviar agora)</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">Agendamento</label>
              <input
                type="datetime-local"
                value={scheduledAt}
                onChange={(e) => setScheduledAt(e.target.value)}
                className="w-full px-3 py-2.5 bg-white border border-background-200 rounded-lg text-sm text-foreground-900 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground-700 mb-1.5">Tipo de público</label>
            <div className="flex gap-2">
              {(['grupos', 'segmento', 'todos'] as const).map((type) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => setTargetType(type)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer border ${
                    targetType === type
                      ? 'bg-primary-50 text-primary-700 border-primary-300'
                      : 'bg-white text-foreground-600 border-background-200 hover:bg-background-50'
                  }`}
                >
                  {type === 'grupos' && <i className="ri-group-line mr-1" />}
                  {type === 'segmento' && <i className="ri-filter-3-line mr-1" />}
                  {type === 'todos' && <i className="ri-global-line mr-1" />}
                  {type === 'grupos' ? 'Grupos' : type === 'segmento' ? 'Segmento' : 'Todos'}
                </button>
              ))}
            </div>
          </div>

          {targetType !== 'todos' && (
            <div>
              <label className="block text-sm font-medium text-foreground-700 mb-2">Selecionar grupos</label>
              <div className="flex flex-wrap gap-2">
                {groups.map((group) => (
                  <button
                    key={group.id}
                    type="button"
                    onClick={() => toggleGroup(group.name)}
                    className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer border ${
                      selectedGroups.includes(group.name)
                        ? 'bg-primary-50 text-primary-700 border-primary-300'
                        : 'bg-white text-foreground-600 border-background-200 hover:bg-background-50'
                    }`}
                  >
                    <i className={`${selectedGroups.includes(group.name) ? 'ri-checkbox-circle-fill' : 'ri-checkbox-blank-circle-line'} mr-1`} />
                    {group.name}
                  </button>
                ))}
              </div>
              {selectedGroups.length === 0 && (
                <p className="text-xs text-amber-600 mt-2">Selecione pelo menos um grupo</p>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 p-5 border-t border-background-100">
          {saveError && (
            <div className="flex-1 flex items-center gap-2 text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">
              <i className="ri-error-warning-line flex-shrink-0" />
              <span>{saveError}</span>
            </div>
          )}
          <button
            type="button"
            onClick={handleClose}
            className="px-4 py-2 text-sm font-medium text-foreground-600 hover:bg-background-100 rounded-lg transition-colors cursor-pointer"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!name.trim() || !messageText.trim() || (targetType !== 'todos' && selectedGroups.length === 0) || saving || uploading}
            className="px-4 py-2 bg-primary-500 hover:bg-primary-600 disabled:bg-primary-300 text-white rounded-lg text-sm font-semibold transition-colors cursor-pointer"
          >
            {saving || uploading ? (
              <span className="flex items-center gap-2">
                <i className="ri-loader-4-line animate-spin" />
                {uploading ? 'Enviando vídeo...' : 'Salvando...'}
              </span>
            ) : (
              campaign ? 'Salvar alterações' : 'Criar campanha'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}